import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl, defineMessages } from 'react-intl';

import CCPopover from 'components/shared/DesignSystem/SlabTransferPortal/Floating/CCPopover';
import AssetTypeToggle from './AssetTypeToggle';
import AssetSizeToggle from './AssetSizeToggle';
import SortOrderToggle from './SortOrderToggle';
import ShowTagsToggle from './ShowTagsToggle';
import styled from 'styled-components';
import { baseButtonStyles } from 'components/shared/DesignSystem/shared/styles';

import TagsOperatorToggle from './TagsOperatorToggle';
import { AND, OR } from '../constants';

const ViewButton = styled.button`
  ${baseButtonStyles}
  display: flex;
  gap: calc(calc(var(--cc_size_text_s) + 1px) * 0.25 + 4px);
  font-family: 'Averta', system-ui, sans-serif;
  color: var(--cc_color_link_subtle);
  font-size: calc(var(--cc_size_text_s) + 1px);
  line-height: 1.1;
  align-items: center;
  justify-content: center;
  border-radius: var(--cc_size_border_radius_l);
  background-color: var(--cc_color_button_background_subtle);
  padding: 0 var(--cc_size_spacing_m);
  height: 40px;
  transition: all cubic-bezier(0.785, 0.135, 0.15, 0.86) 0.2s;

  &:hover {
    background-color: var(--cc_color_button_background_subtle_darken_10);
  }
`;

const Icon = styled.i`
  font-size: calc(calc(var(--cc_size_text_s) + 1px) + 4px);
`;

const SortMenu = styled(CCPopover)`
  display: grid;
  padding: var(--cc_size_spacing_m);
  z-index: 999;
`;
const Divider = styled.div`
  height: var(--cc_size_border_width_s);
  background-color: var(--cc_color_border_default);
  margin: 10px auto;
`;

const messages = defineMessages({
  buttonText: {
    id: 'AssetFeed.ViewMenu.buttonText',
    defaultMessage: 'View',
    description: 'text for button that opens the View Menu',
  },
});

const ViewMenu = ({
  setAssetSize,
  setSortOrder,
  setShowTags,
  setAssetType,
  assetSize,
  sortOrder,
  showTags,
  selectedAssetType,
  hideProjects,
  setTagsOperator,
  tagsOperator,
}) => {
  const { formatMessage } = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const handleToggle = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  return (
    <SortMenu
      trigger={
        <ViewButton type="button" data-testid="view_menu__button">
          <Icon className="mdi mdi-chevron-down" aria-hidden="true" />
          {formatMessage(messages.buttonText)}
        </ViewButton>
      }
      placement="bottom-start"
      id="my-id"
      className="my-class-name"
      isOpen={isOpen}
      onOpenChange={handleToggle}
      mountNodeId="main"
    >
      {hideProjects && (
        <>
          <ShowTagsToggle setShowTags={setShowTags} showTags={showTags} />
          <Divider />
        </>
      )}
        <>
          <TagsOperatorToggle
            selected={tagsOperator}
            onChange={(operator) => setTagsOperator(operator)}
          />
          <Divider />
        </>
        <>
          <AssetTypeToggle
            setAssetType={setAssetType}
            selectedAssetType={selectedAssetType}
          />
          <Divider />
        </>
      <AssetSizeToggle setAssetSize={setAssetSize} assetSize={assetSize} />
      <Divider />
      <SortOrderToggle setSortOrder={setSortOrder} sortOrder={sortOrder} />
    </SortMenu>
  );
};

ViewMenu.propTypes = {
  setAssetSize: PropTypes.func.isRequired,
  setSortOrder: PropTypes.func.isRequired,
  setShowTags: PropTypes.func.isRequired,
  setAssetType: PropTypes.func.isRequired,
  assetSize: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  showTags: PropTypes.bool.isRequired,
  hideProjects: PropTypes.bool.isRequired,
  selectedAssetType: PropTypes.string.isRequired,
  setTagsOperator: PropTypes.func.isRequired,
  tagsOperator: PropTypes.oneOf([AND, OR]).isRequired,
};

export default ViewMenu;
